import { VoiceInput } from '../components/VoiceInput';
// src/modules/WoundCareModule.tsx
import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, CheckCircle, XCircle, Clock, Users,
  FileText, Plus, Trash2, Edit, Save, X, Eye, EyeOff,
  Activity, Heart, Droplet, Shield, Lock, Unlock,
  Clipboard, Calendar, User, Phone, Mail, MapPin,
  FileCheck, FileWarning, FileX, FilePlus, FileMinus,
  Stethoscope, Pill, Syringe, Bandage, Scissors, Thermometer
} from 'lucide-react';
import BodyMap, { BodyPart, Annotation } from './BodyMap';

interface WoundChartEntry {
  id: string;
  resident_id: string;
  resident_name: string;
  room_number: string;
  waterlow_score: number;
  risk_category: 'Active Risk' | 'High Risk' | 'Very High Risk';
  wound_location: 'Sacrum' | 'Left Heel' | 'Right Heel' | 'Ischium' | 'Trochanter' | 'Other';
  ulcer_stage: 'Category 1 (Non-blanchable erythema)' | 'Category 2 (Partial thickness)' | 'Category 3 (Full thickness skin loss)' | 'Category 4 (Full thickness tissue loss)' | 'Unstageable / Deep Tissue Injury';
  dimensions_cm: { length: number; width: number; depth: number };
  exudate_type: 'Serous' | 'Sanguinous' | 'Serosanguinous' | 'Purulent (Triggers IPC Loop)';
  dressing_type_applied: string;
  repositioned_frequency_hours: number;
  next_dressing_change_due: string;
  assigned_nurse_name: string;
  assigned_nurse_nmc_pin: string;
  timestamp: string;
  is_archived: boolean;
  swab_taken_date: string | null;
  gp_antimicrobial_prescription_log: string | null;
  barrier_nursing_isolation_active: boolean;
  wakefield_tvi_referral_checked: boolean;
  pressure_relief_equipment_serial: string;
  pressure_relief_equipment_pressure_setting: string;
  bodyMapAnnotations?: Annotation[];
}

const DEFAULT_WOUNDS: WoundChartEntry[] = [
  {
    id: '1',
    resident_id: 'R001',
    resident_name: 'Margaret Smith',
    room_number: '104',
    waterlow_score: 22,
    risk_category: 'High Risk',
    wound_location: 'Sacrum',
    ulcer_stage: 'Category 2 (Partial thickness)',
    dimensions_cm: { length: 3.5, width: 2.8, depth: 0.5 },
    exudate_type: 'Serosanguinous',
    dressing_type_applied: 'Hydrocolloid',
    repositioned_frequency_hours: 2,
    next_dressing_change_due: new Date(Date.now() + 24*60*60*1000).toISOString(),
    assigned_nurse_name: 'Nurse Sarah',
    assigned_nurse_nmc_pin: '78D4567E',
    timestamp: new Date().toISOString(),
    is_archived: false,
    swab_taken_date: null,
    gp_antimicrobial_prescription_log: null,
    barrier_nursing_isolation_active: false,
    wakefield_tvi_referral_checked: false,
    pressure_relief_equipment_serial: '',
    pressure_relief_equipment_pressure_setting: '',
    bodyMapAnnotations: [],
  },
  {
    id: '2',
    resident_id: 'R003',
    resident_name: 'Patricia Brown',
    room_number: '112',
    waterlow_score: 31,
    risk_category: 'Very High Risk',
    wound_location: 'Left Heel',
    ulcer_stage: 'Category 3 (Full thickness skin loss)',
    dimensions_cm: { length: 5.0, width: 3.2, depth: 1.2 },
    exudate_type: 'Purulent (Triggers IPC Loop)',
    dressing_type_applied: 'Alginate',
    repositioned_frequency_hours: 1,
    next_dressing_change_due: new Date(Date.now() + 12*60*60*1000).toISOString(),
    assigned_nurse_name: 'Nurse John',
    assigned_nurse_nmc_pin: '12A3456B',
    timestamp: new Date().toISOString(),
    is_archived: false,
    swab_taken_date: null,
    gp_antimicrobial_prescription_log: null,
    barrier_nursing_isolation_active: false,
    wakefield_tvi_referral_checked: false,
    pressure_relief_equipment_serial: '',
    pressure_relief_equipment_pressure_setting: '',
    bodyMapAnnotations: [],
  }
];

export function WoundCareModule({ user }: { user: any }) {
  // ─── ALL HOOKS FIRST ──────────────────────────────────────
  const [wounds, setWounds] = useState<WoundChartEntry[]>([]);
  const [selectedWound, setSelectedWound] = useState<WoundChartEntry | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'records' | 'body-map'>('records');
  const [formData, setFormData] = useState<Partial<WoundChartEntry>>({});
  const [showPurulentAlert, setShowPurulentAlert] = useState(false);
  const [showTviMandatory, setShowTviMandatory] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [bodyMapGender, setBodyMapGender] = useState<'male' | 'female'>('male');
  const [bodyMapSide, setBodyMapSide] = useState<'front' | 'back' | 'left' | 'right'>('front');
  const [bodyMapSelectedParts, setBodyMapSelectedParts] = useState<BodyPart[]>([]);
  const [bodyMapAnnotations, setBodyMapAnnotations] = useState<Annotation[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem('rmacs_wound_vault');
    if (stored) {
      try { setWounds(JSON.parse(stored)); } catch { setWounds(DEFAULT_WOUNDS); }
    } else {
      setWounds(DEFAULT_WOUNDS);
      localStorage.setItem('rmacs_wound_vault', JSON.stringify(DEFAULT_WOUNDS));
    }
  }, []);

  // ─── RBAC CHECK (after hooks) ─────────────────────────────
  const allowedRoles = ['SuperAdmin', 'ClinicalLead', 'Registered Nurse'];
  if (!user || !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-[#F7F5EE] rounded-2xl p-8">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center border-2 border-red-200">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-700">❌ Authorization Failure</h2>
          <p className="text-gray-600 mt-2">Clinical credentials required to access Special Category Tissue Viability Records.</p>
          <p className="text-sm text-gray-500 mt-1">Your role: {user?.role || 'Unknown'}</p>
        </div>
      </div>
    );
  }

  // ─── HELPERS & HANDLERS ──────────────────────────────────
  const getRiskCategory = (score: number): 'Active Risk' | 'High Risk' | 'Very High Risk' => {
    if (score >= 20) return 'Very High Risk';
    if (score >= 15) return 'High Risk';
    return 'Active Risk';
  };

  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

  const resetForm = () => {
    setFormData({
      resident_id: '',
      resident_name: '',
      room_number: '',
      waterlow_score: 0,
      risk_category: 'Active Risk',
      wound_location: 'Sacrum',
      ulcer_stage: 'Category 1 (Non-blanchable erythema)',
      dimensions_cm: { length: 0, width: 0, depth: 0 },
      exudate_type: 'Serous',
      dressing_type_applied: '',
      repositioned_frequency_hours: 2,
      next_dressing_change_due: '',
      assigned_nurse_name: user?.name || '',
      assigned_nurse_nmc_pin: '',
      is_archived: false,
      swab_taken_date: null,
      gp_antimicrobial_prescription_log: null,
      barrier_nursing_isolation_active: false,
      wakefield_tvi_referral_checked: false,
      pressure_relief_equipment_serial: '',
      pressure_relief_equipment_pressure_setting: '',
      bodyMapAnnotations: [],
    });
    setBodyMapSelectedParts([]);
    setBodyMapAnnotations([]);
    setShowPurulentAlert(false);
    setShowTviMandatory(false);
    setIsAdding(false);
    setIsEditing(false);
    setSelectedWound(null);
    setActiveTab('records');
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }
    if (name.startsWith('dimensions_')) {
      const key = name.replace('dimensions_', '');
      setFormData(prev => ({
        ...prev,
        dimensions_cm: {
          length: prev.dimensions_cm?.length ?? 0,
          width: prev.dimensions_cm?.width ?? 0,
          depth: prev.dimensions_cm?.depth ?? 0,
          [key]: parseFloat(value) || 0
        }
      }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'waterlow_score') {
      const score = parseFloat(value) || 0;
      setFormData(prev => ({ ...prev, risk_category: getRiskCategory(score) }));
    }
    if (name === 'exudate_type') {
      setShowPurulentAlert(value === 'Purulent (Triggers IPC Loop)');
    }
    if (name === 'ulcer_stage') {
      setShowTviMandatory(value.includes('Category 3') || value.includes('Category 4'));
    }
  };

  const handleAdd = () => {
    const newEntry: WoundChartEntry = {
      id: generateId(),
      resident_id: formData.resident_id || 'R001',
      resident_name: formData.resident_name || 'Unknown',
      room_number: formData.room_number || 'N/A',
      waterlow_score: formData.waterlow_score || 0,
      risk_category: getRiskCategory(formData.waterlow_score || 0),
      wound_location: formData.wound_location as any || 'Sacrum',
      ulcer_stage: formData.ulcer_stage as any || 'Category 1 (Non-blanchable erythema)',
      dimensions_cm: formData.dimensions_cm || { length: 0, width: 0, depth: 0 },
      exudate_type: formData.exudate_type as any || 'Serous',
      dressing_type_applied: formData.dressing_type_applied || '',
      repositioned_frequency_hours: formData.repositioned_frequency_hours || 2,
      next_dressing_change_due: formData.next_dressing_change_due || new Date().toISOString(),
      assigned_nurse_name: formData.assigned_nurse_name || user?.name || '',
      assigned_nurse_nmc_pin: formData.assigned_nurse_nmc_pin || '',
      timestamp: new Date().toISOString(),
      is_archived: false,
      swab_taken_date: formData.swab_taken_date || null,
      gp_antimicrobial_prescription_log: formData.gp_antimicrobial_prescription_log || null,
      barrier_nursing_isolation_active: formData.barrier_nursing_isolation_active || false,
      wakefield_tvi_referral_checked: formData.wakefield_tvi_referral_checked || false,
      pressure_relief_equipment_serial: formData.pressure_relief_equipment_serial || '',
      pressure_relief_equipment_pressure_setting: formData.pressure_relief_equipment_pressure_setting || '',
      bodyMapAnnotations: bodyMapAnnotations,
    };
    saveWounds([newEntry, ...wounds]);
    setMessage({ text: '✅ Wound chart added successfully', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
    resetForm();
  };

  const handleUpdate = () => {
    if (!selectedWound) return;
    const updated = wounds.map(w => {
      if (w.id === selectedWound.id) {
        return {
          ...w,
          ...formData,
          risk_category: getRiskCategory(formData.waterlow_score || w.waterlow_score),
          bodyMapAnnotations: bodyMapAnnotations
        };
      }
      return w;
    });
    saveWounds(updated);
    setMessage({ text: '✅ Wound chart updated', type: 'success' });
    setTimeout(() => setMessage(null), 3000);
    resetForm();
  };

  const handleArchive = (id: string) => {
    const updated = wounds.map(w => w.id === id ? { ...w, is_archived: !w.is_archived } : w);
    saveWounds(updated);
    setMessage({ text: 'Archive toggled', type: 'warning' });
    setTimeout(() => setMessage(null), 2000);
  };

  const handleDelete = (id: string) => {
    if (!window.confirm('Permanently delete this wound record? This cannot be undone.')) return;
    const filtered = wounds.filter(w => w.id !== id);
    saveWounds(filtered);
    setMessage({ text: '🗑️ Record deleted', type: 'error' });
    setTimeout(() => setMessage(null), 2000);
  };

  const loadWound = (wound: WoundChartEntry) => {
    setSelectedWound(wound);
    setFormData({ ...wound });
    setBodyMapSelectedParts(wound.bodyMapAnnotations?.map(ann => ({ id: ann.id, name: `Pin at ${ann.x}%,${ann.y}%` })) || []);
    setBodyMapAnnotations(wound.bodyMapAnnotations || []);
    setShowPurulentAlert(wound.exudate_type === 'Purulent (Triggers IPC Loop)');
    setShowTviMandatory(wound.ulcer_stage.includes('Category 3') || wound.ulcer_stage.includes('Category 4'));
    setIsEditing(true);
    setIsAdding(false);
    setActiveTab('body-map');
  };

  const saveWounds = (data: WoundChartEntry[]) => {
    localStorage.setItem('rmacs_wound_vault', JSON.stringify(data));
    setWounds(data);
  };

  const activeWounds = wounds.filter(w => !w.is_archived);
  const archivedWounds = wounds.filter(w => w.is_archived);

  // ─── RENDER ──────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <Activity className="w-6 h-6" /> Wound Care & Skin Integrity
          </h2>
          <p className="text-[#6B7A76]">CQC Reg 12 – clinical documentation, pressure ulcer staging, and tissue viability tracking</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { resetForm(); setIsAdding(true); setFormData(prev => ({ ...prev, assigned_nurse_name: user?.name || '' })); }} className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Wound Chart
          </button>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[#E1DCCD] pb-2">
        <button onClick={() => setActiveTab('records')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'records' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>
          <FileText className="w-4 h-4 inline" /> Records
        </button>
        <button onClick={() => setActiveTab('body-map')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'body-map' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>
          <Activity className="w-4 h-4 inline" /> Body Map
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded-xl text-sm ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
          {message.text}
        </div>
      )}

      {activeTab === 'body-map' && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Interactive Body Map</h3>
          <p className="text-sm text-gray-500 mb-4">Click on any body part to add clinical notes, photos, or videos.</p>
          <div className="flex gap-2 mb-4 flex-wrap">
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'front' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('front')}>Front</button>
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'back' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('back')}>Back</button>
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'left' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('left')}>Left</button>
            <button className={`px-3 py-1 rounded ${bodyMapSide === 'right' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`} onClick={() => setBodyMapSide('right')}>Right</button>
          </div>
          <BodyMap
            selectedParts={bodyMapSelectedParts}
            onPartSelect={(part) => setBodyMapSelectedParts(prev => [...prev, part])}
            onPartDeselect={(part) => setBodyMapSelectedParts(prev => prev.filter(p => p.id !== part.id))}
            annotations={bodyMapAnnotations}
            onAnnotationsChange={setBodyMapAnnotations}
          />
          <div className="mt-4 p-3 bg-[#F7F5EE] rounded-lg text-sm text-gray-600">
            <strong>Marked areas:</strong> {bodyMapSelectedParts.map(p => p.name).join(', ') || 'None'}
          </div>
        </div>
      )}

      {activeTab === 'records' && (
        <>
          {(isAdding || isEditing) && (
            <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
              <h3 className="text-lg font-bold text-[#123B36] mb-4">{isAdding ? 'New Wound Chart' : 'Edit Wound Chart'}</h3>
              {showPurulentAlert && (
                <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-red-700">IPC Alert: Potential Wound Infection Detected</p>
                    <p className="text-sm text-red-600">Purulent exudate selected – automated secondary workflow activated below.</p>
                  </div>
                </div>
              )}
              {showTviMandatory && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-300 rounded-lg flex items-start gap-3">
                  <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-blue-700">Wakefield TVI Referral Required</p>
                    <p className="text-sm text-blue-600">Category 3/4 ulcer detected – you must complete the Mid Yorkshire Tissue Viability Joint Protocol Form.</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Resident Name</label>
                  <VoiceInput name="resident_name" value={formData.resident_name || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Resident ID</label>
                  <VoiceInput name="resident_id" value={formData.resident_id || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Room Number</label>
                  <VoiceInput name="room_number" value={formData.room_number || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Waterlow Score</label>
                  <input type="number" name="waterlow_score" value={formData.waterlow_score || 0} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Wound Location</label>
                  <select name="wound_location" value={formData.wound_location || 'Sacrum'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Sacrum">Sacrum</option>
                    <option value="Left Heel">Left Heel</option>
                    <option value="Right Heel">Right Heel</option>
                    <option value="Ischium">Ischium</option>
                    <option value="Trochanter">Trochanter</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Ulcer Stage</label>
                  <select name="ulcer_stage" value={formData.ulcer_stage || 'Category 1 (Non-blanchable erythema)'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Category 1 (Non-blanchable erythema)">Category 1</option>
                    <option value="Category 2 (Partial thickness)">Category 2</option>
                    <option value="Category 3 (Full thickness skin loss)">Category 3</option>
                    <option value="Category 4 (Full thickness tissue loss)">Category 4</option>
                    <option value="Unstageable / Deep Tissue Injury">Unstageable / Deep Tissue Injury</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Exudate Type</label>
                  <select name="exudate_type" value={formData.exudate_type || 'Serous'} onChange={handleFormChange} className="w-full border rounded px-3 py-2">
                    <option value="Serous">Serous</option>
                    <option value="Sanguinous">Sanguinous</option>
                    <option value="Serosanguinous">Serosanguinous</option>
                    <option value="Purulent (Triggers IPC Loop)">Purulent (Triggers IPC Loop)</option>
                  </select>
                </div>
                <div><label className="block text-sm font-medium text-gray-700">Dressing Type Applied</label>
                  <VoiceInput name="dressing_type_applied" value={formData.dressing_type_applied || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Repositioning Frequency (hours)</label>
                  <input type="number" name="repositioned_frequency_hours" value={formData.repositioned_frequency_hours || 2} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Next Dressing Change Due</label>
                  <input type="datetime-local" name="next_dressing_change_due" value={formData.next_dressing_change_due || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Assigned Nurse Name</label>
                  <VoiceInput name="assigned_nurse_name" value={formData.assigned_nurse_name || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                <div><label className="block text-sm font-medium text-gray-700">NMC PIN</label>
                  <VoiceInput name="assigned_nurse_nmc_pin" value={formData.assigned_nurse_nmc_pin || ''} onChange={handleFormChange} className="w-full border rounded px-3 py-2" /></div>
                {showPurulentAlert && (
                  <div className="col-span-2 border border-red-200 bg-red-50 p-3 rounded-lg">
                    <h4 className="font-bold text-red-700">IPC Secondary Workflow</h4>
                    <div className="grid grid-cols-2 gap-4 mt-2">
                      <div><label className="block text-xs text-gray-700">Swab Taken Date</label>
                        <input type="date" name="swab_taken_date" value={formData.swab_taken_date || ''} onChange={handleFormChange} className="w-full border rounded px-2 py-1 text-sm" /></div>
                      <div><label className="block text-xs text-gray-700">GP Antimicrobial Prescription Log</label>
                        <VoiceInput name="gp_antimicrobial_prescription_log" value={formData.gp_antimicrobial_prescription_log || ''} onChange={handleFormChange} className="w-full border rounded px-2 py-1 text-sm" /></div>
                      <div className="col-span-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input type="checkbox" name="barrier_nursing_isolation_active" checked={formData.barrier_nursing_isolation_active || false} onChange={handleFormChange} />
                          Barrier Nursing / Isolation Active
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-span-2 border border-blue-200 bg-blue-50 p-3 rounded-lg">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" name="wakefield_tvi_referral_checked" checked={formData.wakefield_tvi_referral_checked || false} onChange={handleFormChange} />
                    <span className="font-medium text-blue-800">Mid Yorkshire Tissue Viability Referral Transmitted via Secure Gateway</span>
                  </label>
                  {showTviMandatory && <p className="text-xs text-blue-600 mt-1">⛔ Mandatory for Category 3/4 – you cannot lock the record without checking this box.</p>}
                </div>
                <div className="col-span-2 border border-purple-200 bg-purple-50 p-3 rounded-lg">
                  <h4 className="font-bold text-purple-700">Pressure‑Relief Equipment (Bradford / Kirklees ICB)</h4>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div><label className="block text-xs text-gray-700">Serial Number</label>
                      <VoiceInput name="pressure_relief_equipment_serial" value={formData.pressure_relief_equipment_serial || ''} onChange={handleFormChange} className="w-full border rounded px-2 py-1 text-sm" /></div>
                    <div><label className="block text-xs text-gray-700">Working Pressure Setting</label>
                      <VoiceInput name="pressure_relief_equipment_pressure_setting" value={formData.pressure_relief_equipment_pressure_setting || ''} onChange={handleFormChange} className="w-full border rounded px-2 py-1 text-sm" /></div>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Dimensions (cm)</label>
                  <div className="flex gap-2">
                    <input type="number" placeholder="Length" value={formData.dimensions_cm?.length || 0} onChange={(e) => handleFormChange({ target: { name: 'dimensions_length', value: e.target.value } } as any)} className="w-1/3 border rounded px-3 py-2" />
                    <input type="number" placeholder="Width" value={formData.dimensions_cm?.width || 0} onChange={(e) => handleFormChange({ target: { name: 'dimensions_width', value: e.target.value } } as any)} className="w-1/3 border rounded px-3 py-2" />
                    <input type="number" placeholder="Depth" value={formData.dimensions_cm?.depth || 0} onChange={(e) => handleFormChange({ target: { name: 'dimensions_depth', value: e.target.value } } as any)} className="w-1/3 border rounded px-3 py-2" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={isAdding ? handleAdd : handleUpdate} className="bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28]">
                  {isAdding ? 'Save' : 'Update'}
                </button>
                <button onClick={resetForm} className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
            <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
              <h3 className="font-bold text-[#123B36]">Active Wound Charts</h3>
              <span className="text-sm text-gray-500">{activeWounds.length} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F7F5EE]">
                  <tr>
                    <th className="p-3 text-left">Resident</th>
                    <th className="p-3 text-left">Location</th>
                    <th className="p-3 text-left">Stage</th>
                    <th className="p-3 text-left">Exudate</th>
                    <th className="p-3 text-left">Waterlow</th>
                    <th className="p-3 text-left">Nurse</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activeWounds.map(w => {
                    const hasAnnotations = w.bodyMapAnnotations && w.bodyMapAnnotations.length > 0;
                    return (
                      <tr key={w.id} className="border-b border-[#E1DCCD] hover:bg-[#F7F5EE] transition">
                        <td className="p-3">
                          <div>
                            <span className="font-medium">{w.resident_name}</span>
                            {hasAnnotations && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">📌 {w.bodyMapAnnotations!.length} pins</span>}
                          </div>
                        </td>
                        <td className="p-3">{w.wound_location}</td>
                        <td className="p-3">{w.ulcer_stage}</td>
                        <td className="p-3">{w.exudate_type}</td>
                        <td className="p-3">{w.waterlow_score}</td>
                        <td className="p-3">{w.assigned_nurse_name}</td>
                        <td className="p-3">
                          <button onClick={() => loadWound(w)} className="p-1 bg-blue-100 rounded text-blue-700 hover:bg-blue-200 text-xs px-2">Edit</button>
                          <button onClick={() => handleArchive(w.id)} className="p-1 bg-gray-100 rounded text-gray-700 hover:bg-gray-200 text-xs px-2 ml-1">Archive</button>
                          <button onClick={() => handleDelete(w.id)} className="p-1 bg-red-100 rounded text-red-700 hover:bg-red-200 text-xs px-2 ml-1">Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {archivedWounds.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
              <div className="bg-gray-50 px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
                <h3 className="font-bold text-[#123B36]">Archived Records</h3>
                <span className="text-sm text-gray-500">{archivedWounds.length} records</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr><th className="p-3 text-left">Resident</th><th className="p-3 text-left">Location</th><th className="p-3 text-left">Stage</th><th className="p-3 text-left">Actions</th></tr>
                  </thead>
                  <tbody>
                    {archivedWounds.map(w => (
                      <tr key={w.id} className="border-b border-[#E1DCCD]">
                        <td className="p-3">{w.resident_name}</td>
                        <td className="p-3">{w.wound_location}</td>
                        <td className="p-3">{w.ulcer_stage}</td>
                        <td className="p-3">
                          <button onClick={() => handleArchive(w.id)} className="p-1 bg-blue-100 rounded text-blue-700 hover:bg-blue-200 text-xs px-2">Unarchive</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default WoundCareModule;